<script setup>
import { computed, ref } from 'vue'
import { ImageUp, Trash2 } from 'lucide-vue-next'
import UploadButton from '@Uploadable/components/UploadButton.vue'
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { Alert, AlertDescription } from '../../../../../../../resources/js/components/ui/alert/index.js'
import { Avatar, AvatarFallback, AvatarImage } from '../../../../../../../resources/js/components/ui/avatar/index.js'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { Input } from '../../../../../../../resources/js/components/ui/input/index.js'
import { Textarea } from '../../../../../../../resources/js/components/ui/textarea/index.js'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { useConversationStore } from '../../stores/conversationStore.js'

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['conversationActionStarted', 'conversationActionFailed', 'conversationUpdated'])

const { componentRef: dialog, openModal, closeModal } = useModalControl()
const conversationStore = useConversationStore()

const conversationUploadableType = 'Umisis\\Chat\\Models\\Conversation'
const draftTitle = ref('')
const draftAvatarPath = ref('')
const draftMetadata = ref('')
const actionError = ref('')
const metadataValidationMessage = ref('')
const isUploadingAvatar = ref(false)

const initials = computed(() => {
  return String(draftTitle.value || props.conversation.title || 'G')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('') || 'G'
})

const avatarPreviewPath = computed(() => draftAvatarPath.value || props.conversation.avatar_path || '')

const isSubmitDisabled = computed(() => {
  return conversationStore.isUpdatingConversation || isUploadingAvatar.value || draftTitle.value.trim().length === 0
})

const onShow = () => {
  draftTitle.value = props.conversation.title || ''
  draftAvatarPath.value = props.conversation.avatar_path || ''
  draftMetadata.value = props.conversation.metadata ? JSON.stringify(props.conversation.metadata, null, 2) : ''
  actionError.value = ''
  metadataValidationMessage.value = ''
  isUploadingAvatar.value = false
}

const handleAvatarUploadStarted = () => {
  actionError.value = ''
  isUploadingAvatar.value = true
}

const handleAvatarUploaded = (payload) => {
  const uploadedFile = Array.isArray(payload) ? payload[0] : payload
  const uploadedAvatarUrl = uploadedFile?.url_small || uploadedFile?.url_medium || uploadedFile?.url || uploadedFile?.url_secret

  if (!uploadedAvatarUrl) {
    actionError.value = 'The image uploaded, but no avatar URL was returned.'
    return
  }

  draftAvatarPath.value = uploadedAvatarUrl
}

const handleAvatarUploadError = (error) => {
  actionError.value = errorMessageFrom(error, 'Unable to upload group avatar.')
  emit('conversationActionFailed', actionError.value)
}

const handleAvatarUploadFinished = () => {
  isUploadingAvatar.value = false
}

const removeAvatar = () => {
  draftAvatarPath.value = ''
}

const handleOk = async (bvEvent) => {
  if (bvEvent) {
    bvEvent.preventDefault()
  }

  await updateConversationDetails()
}

const updateConversationDetails = async () => {
  let metadata = null
  actionError.value = ''
  metadataValidationMessage.value = ''

  if (draftMetadata.value.trim()) {
    try {
      metadata = JSON.parse(draftMetadata.value)
    } catch {
      metadataValidationMessage.value = 'Metadata must be valid JSON.'
      return
    }
  }

  emit('conversationActionStarted')

  try {
    const conversation = await conversationStore.updateConversation(props.conversation.id, {
      title: draftTitle.value.trim(),
      avatar_path: draftAvatarPath.value || null,
      metadata,
    })

    emit('conversationUpdated', conversation)
    closeModal()
  } catch (error) {
    actionError.value = errorMessageFrom(error, 'Unable to update conversation.')
    emit('conversationActionFailed', actionError.value)
  }
}

const errorMessageFrom = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors

  if (validationErrors) {
    const messages = Object.values(validationErrors).flat().filter(Boolean)

    if (messages.length > 0) {
      return messages.join(' ')
    }
  }

  return error?.response?.data?.message || error?.message || fallback
}

defineExpose({
  openModal,
  closeModal,
})
</script>

<template>
  <UmisisModal ref="dialog" title="Edit Group" description="Update the group name, avatar, and metadata." ok-title="Save changes" cancel-title="Cancel" :ok-disabled="isSubmitDisabled" @ok="handleOk" @show="onShow">
    <div class="flex flex-col gap-4">
      <Alert v-if="actionError" variant="destructive">
        <AlertDescription>{{ actionError }}</AlertDescription>
      </Alert>

      <div class="flex items-center gap-4">
        <Avatar size="base">
          <AvatarImage v-if="avatarPreviewPath" :src="avatarPreviewPath" />
          <AvatarFallback>{{ initials }}</AvatarFallback>
        </Avatar>

        <div class="flex min-w-0 flex-1 flex-col gap-2">
          <div class="flex flex-wrap gap-2">
            <UploadButton :uploadable-id="conversation.id" :uploadable-type="conversationUploadableType" images-only label="Upload image" uploading-label="Uploading..." :disabled="conversationStore.isUpdatingConversation" @upload-start="handleAvatarUploadStarted" @uploaded="handleAvatarUploaded" @error="handleAvatarUploadError" @upload-finish="handleAvatarUploadFinished">
              <template #default="{ isUploading }">
                <span class="inline-flex items-center gap-2">
                  <ImageUp class="h-4 w-4" />
                  {{ isUploading ? 'Uploading...' : 'Upload image' }}
                </span>
              </template>
            </UploadButton>

            <Button v-if="avatarPreviewPath" type="button" variant="outline" size="sm" class="gap-2" :disabled="conversationStore.isUpdatingConversation || isUploadingAvatar" @click="removeAvatar">
              <Trash2 class="h-4 w-4" />
              Remove
            </Button>
          </div>
          <p class="text-xs text-muted-foreground">
            Upload a square image for the clearest avatar.
          </p>
        </div>
      </div>

      <Field class="gap-1">
        <FieldLabel for="chat-group-edit-title">
          Group title
        </FieldLabel>
        <Input id="chat-group-edit-title" v-model="draftTitle" placeholder="Group title" :is-error="draftTitle.trim().length === 0" />
        <FieldDescription v-if="draftTitle.trim().length === 0" class="font-medium text-destructive">
          The conversation title is required.
        </FieldDescription>
      </Field>

      <Field class="gap-1">
        <FieldLabel for="chat-group-edit-metadata">
          Metadata JSON
        </FieldLabel>
        <Textarea id="chat-group-edit-metadata" v-model="draftMetadata" rows="3" placeholder="Metadata JSON" :class="metadataValidationMessage ? 'border-destructive focus-visible:ring-destructive' : ''" />
        <FieldDescription v-if="metadataValidationMessage" class="font-medium text-destructive">
          {{ metadataValidationMessage }}
        </FieldDescription>
      </Field>
    </div>
  </UmisisModal>
</template>
